import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/common/Logo";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import firestoreService from "../services/firestoreService";
import { ROUTES } from "../utils/constants";

const ReferralReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralData, setReferralData] = useState({
    topReferrers: [],
    recentReferrals: [],
    referralStats: {
      totalReferrals: 0,
      totalReferrers: 0,
      approvedReferrals: 0,
      pendingReferrals: 0,
      rejectedReferrals: 0,
    },
    referralNetwork: [],
  });
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await firestoreService.getReferralReports();
      setReferralData(data);
    } catch (err) {
      console.error("Error loading referral data:", err);
      setError("Failed to load referral reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReferrerClick = (referrer) => {
    setSelectedReferrer(referrer);
  };

  const filteredReferrals = referralData.recentReferrals.filter((referral) => {
    const matchesSearch =
      referral.referredUserName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      referral.referrerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.referredUserEmail
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || referral.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="admin-dashboard referral-reports-wide">
        <div className="dashboard-header">
          <div className="container">
            <div className="header-content">
              <div className="header-left">
                <Logo size="medium" />
                <h1 className="dashboard-title">Referral Reports</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="dashboard-content">
          <div className="container">
            <LoadingSpinner size="large" message="Loading referral data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard referral-reports-wide">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <Logo size="medium" />
              <h1 className="dashboard-title">Referral Reports</h1>
            </div>
            <div className="dashboard-actions">
              <button
                onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
                className="nav-button"
                type="button"
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        <div className="container">
          {/* Error Message */}
          {error && (
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          )}

          {/* Statistics Cards */}
          <div className="statistics-section">
            <div className="stats-grid">
              <div className="stat-card total">
                <div className="stat-number">
                  {referralData.referralStats.totalReferrals}
                </div>
                <div className="stat-label">Total Referrals</div>
              </div>
              <div className="stat-card approved">
                <div className="stat-number">
                  {referralData.referralStats.totalReferrers}
                </div>
                <div className="stat-label">Active Referrers</div>
              </div>
              <div className="stat-card approved">
                <div className="stat-number">
                  {referralData.referralStats.approvedReferrals}
                </div>
                <div className="stat-label">Approved</div>
              </div>
              <div className="stat-card pending">
                <div className="stat-number">
                  {referralData.referralStats.pendingReferrals}
                </div>
                <div className="stat-label">Pending</div>
              </div>
              <div className="stat-card rejected">
                <div className="stat-number">
                  {referralData.referralStats.rejectedReferrals}
                </div>
                <div className="stat-label">Rejected</div>
              </div>
            </div>
          </div>

          {/* Top Referrers Section */}
          <div className="registration-table-container">
            <div className="table-controls">
              <h2 style={{ margin: 0, color: "var(--text-primary)" }}>
                Top Referrers
              </h2>
            </div>
            <div className="table-wrapper">
              <table className="registration-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Location</th>
                    <th>Total Referrals</th>
                    <th>Success Rate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referralData.topReferrers.map((referrer, index) => (
                    <tr key={referrer.id} className="registration-row">
                      <td className="registration-cell">
                        <strong>#{index + 1}</strong>
                      </td>
                      <td className="registration-cell">
                        <strong>{referrer.name}</strong>
                      </td>
                      <td className="registration-cell">{referrer.email}</td>
                      <td className="registration-cell">
                        {referrer.city}, {referrer.country}
                      </td>
                      <td className="registration-cell">
                        <strong>{referrer.totalReferrals}</strong>
                      </td>
                      <td className="registration-cell">
                        <span
                          className={`status-badge ${referrer.successRate >= 70 ? "success" : referrer.successRate >= 40 ? "secondary" : "danger"}`}
                        >
                          {referrer.successRate}%
                        </span>
                      </td>
                      <td className="registration-cell actions-cell">
                        <button
                          onClick={() => handleReferrerClick(referrer)}
                          className="btn btn-sm btn-primary"
                          type="button"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referralData.topReferrers.length === 0 && (
                <div className="no-results">
                  <p>No referrers found.</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Referrals Section */}
          <div className="registration-table-container">
            <div className="table-controls">
              <h2 style={{ margin: 0, color: "var(--text-primary)" }}>
                Recent Referrals
              </h2>
              <div className="table-filters">
                <input
                  type="text"
                  placeholder="Search referrals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="status-select"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div className="table-wrapper">
              <table className="registration-table">
                <thead>
                  <tr>
                    <th>Referred User</th>
                    <th>Referrer</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferrals.map((referral) => (
                    <tr key={referral.id} className="registration-row">
                      <td className="registration-cell">
                        <div>
                          <strong>{referral.referredUserName}</strong>
                          <br />
                          <small style={{ color: "var(--text-secondary)" }}>
                            {referral.referredUserEmail}
                          </small>
                        </div>
                      </td>
                      <td className="registration-cell">
                        <div>
                          <strong>{referral.referrerName}</strong>
                          <br />
                          <small style={{ color: "var(--text-secondary)" }}>
                            {referral.referrerEmail}
                          </small>
                        </div>
                      </td>
                      <td className="registration-cell">
                        {new Date(referral.referredAt).toLocaleDateString()}
                      </td>
                      <td className="registration-cell">
                        <span
                          className={`status-badge status-${referral.status}`}
                        >
                          {referral.status.charAt(0).toUpperCase() +
                            referral.status.slice(1)}
                        </span>
                      </td>
                      <td className="registration-cell">
                        {referral.referredUserCity},{" "}
                        {referral.referredUserCountry}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredReferrals.length === 0 && (
                <div className="no-results">
                  <p>No referrals found matching your criteria.</p>
                </div>
              )}
            </div>
          </div>

          {/* Referral Network Section */}
          <div className="registration-table-container">
            <div className="table-controls">
              <h2 style={{ margin: 0 }}>Referral Network</h2>
            </div>
            <div className="network-node-container">
              {referralData.referralNetwork.map((node) => (
                <div key={node.id} className="network-node-card">
                  <div style={{ marginBottom: "1rem" }}>
                    <h4
                      style={{
                        margin: "0 0 0.5rem 0",
                        color: "var(--text-primary)",
                        fontSize: "1.125rem",
                        fontWeight: "600",
                      }}
                    >
                      {node.name}
                    </h4>
                    <p
                      style={{
                        margin: "0.25rem 0",
                        color: "var(--text-secondary)",
                        fontSize: "0.875rem",
                      }}
                    >
                      {node.email}
                    </p>
                    <span
                      className="status-badge success"
                      style={{ marginTop: "0.5rem", display: "inline-block" }}
                    >
                      {node.referralCount} referrals
                    </span>
                  </div>
                  {node.referrals.length > 0 && (
                    <div
                      style={{
                        marginTop: "1rem",
                        paddingTop: "1rem",
                        borderTop: "1px solid var(--border-primary)",
                      }}
                    >
                      {node.referrals.map((referral) => (
                        <div
                          key={referral.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "1rem",
                            padding: "0.75rem",
                            backgroundColor: "var(--bg-primary)",
                            borderRadius: "0.5rem",
                            marginBottom: "0.5rem",
                            border: "1px solid var(--border-primary)",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--color-primary)";
                            e.currentTarget.style.transform = "translateX(4px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--border-primary)";
                            e.currentTarget.style.transform = "translateX(0)";
                          }}
                        >
                          <span
                            style={{
                              color: "var(--text-primary)",
                              fontWeight: "500",
                            }}
                          >
                            → {referral.name}
                          </span>
                          <span
                            className={`status-badge status-${referral.status}`}
                          >
                            {referral.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {referralData.referralNetwork.length === 0 && (
                <div className="no-results" style={{ gridColumn: "1 / -1" }}>
                  <p>No referral network data available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Referrer Detail Modal */}
      {selectedReferrer && (
        <div
          className="receipt-modal-overlay"
          onClick={() => setSelectedReferrer(null)}
        >
          <div
            className="receipt-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "900px" }}
          >
            <div className="receipt-modal-header">
              <h3>Referrer Details</h3>
              <button
                className="close-modal-btn"
                onClick={() => setSelectedReferrer(null)}
                type="button"
              >
                ×
              </button>
            </div>
            <div
              className="receipt-modal-content"
              style={{ padding: "1.5rem" }}
            >
              <div style={{ marginBottom: "2rem", marginTop: "7.5rem" }}>
                <h4
                  style={{
                    margin: "0 0 1rem 0",
                    color: "var(--text-primary)",
                  }}
                >
                  {selectedReferrer.name}
                </h4>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>📧 Email:</strong> {selectedReferrer.email}
                </p>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>📱 Phone:</strong> {selectedReferrer.phone}
                </p>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>📍 Location:</strong> {selectedReferrer.city},{" "}
                  {selectedReferrer.country}
                </p>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>🎯 Total Referrals:</strong>{" "}
                  {selectedReferrer.totalReferrals}
                </p>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>📈 Success Rate:</strong>{" "}
                  {selectedReferrer.successRate}%
                </p>
                <p style={{ margin: "0.5rem 0", color: "var(--text-primary)" }}>
                  <strong>📅 Joined:</strong>{" "}
                  {new Date(selectedReferrer.joinedAt).toLocaleDateString()}
                </p>
              </div>

              <div>
                <h5
                  style={{
                    margin: "0 0 1rem 0",
                    color: "var(--text-primary)",
                    paddingBottom: "0.5rem",
                    borderBottom: "1px solid var(--border-primary)",
                  }}
                >
                  Referrals Made
                </h5>
                {selectedReferrer.referrals.map((referral) => (
                  <div
                    key={referral.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.75rem",
                      backgroundColor: "var(--bg-secondary)",
                      borderRadius: "0.375rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: "500",
                      }}
                    >
                      {referral.name}
                    </span>
                    <span className={`status-badge status-${referral.status}`}>
                      {referral.status}
                    </span>
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.75rem",
                      }}
                    >
                      {new Date(referral.date).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="receipt-modal-footer">
              <button
                onClick={() => setSelectedReferrer(null)}
                className="btn btn-secondary"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralReports;
